import type { ReactNode } from "react"
import {
  useCallback,
  useMemo,
  useRef,
  useState,
} from "react"

import type { OptionAttributes } from "./DropdownOptionAttributesContext"
import { DropdownOptionAttributesContext } from "./DropdownOptionAttributesContext"

interface Props {
  children?: ReactNode;
  currentValue?: number | string;
  currentSelectionId?: string;
}

/*
 * this element provides a context that allows child elements to subscribe to attributes
 * of the currently selected option
 */
export function DropdownOptionAttributesContextProvider(props: Props) {
  const {
    children,
    currentSelectionId,
  } = props

  // this holds the state of the option attributes
  const [
    optionAttributesById,
    setOptionAttributesById,
  ] = useState<Record<string, OptionAttributes>>({})

  // this ref tracks the state of optionAttributesById
  const optionAttributesByIdRef = useRef(optionAttributesById)

  // this function allows an option element to register its attributes
  const registerOptionAttributes = useCallback(
    (
      id: string,
      attributes: OptionAttributes,
    ) => {
      optionAttributesByIdRef.current[id] = attributes
      setOptionAttributesById({ ...optionAttributesByIdRef.current })
    },
    [
      setOptionAttributesById,
      optionAttributesByIdRef,
    ],
  )

  // this function allows an option element to deregister its attributes
  const deregisterOptionAttributes = useCallback(
    (id: string) => {
      delete optionAttributesByIdRef.current[id]
      setOptionAttributesById({ ...optionAttributesByIdRef.current })
    },
    [
      setOptionAttributesById,
      optionAttributesByIdRef,
    ],
  )

  // these are the attributes of the currently selected option
  const currentSelectionOptionAttributes =
    currentSelectionId === undefined ?
      undefined :
      optionAttributesById[currentSelectionId]

  // this is the value provided by DropdownOptionAttributesContext
  const value = useMemo(
    () => ({
      registerOptionAttributes,
      deregisterOptionAttributes,
      currentSelectionOptionAttributes,
    }),
    [
      registerOptionAttributes,
      deregisterOptionAttributes,
      currentSelectionOptionAttributes,
    ],
  )

  return (
    <DropdownOptionAttributesContext.Provider value={value}>
      {children}
    </DropdownOptionAttributesContext.Provider>
  )
}
